export type SkipDirection = "forward" | "backward";

/**
 * Decides which way to step over a page the patient must not see.
 *
 * - `previousIndex === null` means no navigation has happened yet: the
 *   questionnaire is resuming straight onto a hidden page (for example the
 *   trailing automatic consent pages after Back from signup or checkout).
 *   Stepping backward lands on the last page the patient can actually see;
 *   stepping forward would walk off the end of the questionnaire. The very
 *   first page is the only exception, as there is nothing before it.
 * - A repeat render at the same index (`previousIndex === currentIndex`) is a
 *   stale re-render while a forward skip is in flight, not a backward move;
 *   treating it as backward bounced the patient to the page they just left
 *   (ENG-744).
 * - Otherwise the page is skipped in the direction the patient moved.
 */
export const determineSkipDirection = (
  previousIndex: number | null,
  currentIndex: number
): SkipDirection => {
  const questionnaireStart = currentIndex === 0;
  const isResumingOntoHiddenPage = previousIndex === null && !questionnaireStart;
  const movedBackward = previousIndex !== null && previousIndex > currentIndex;

  return isResumingOntoHiddenPage || movedBackward ? "backward" : "forward";
};
